import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { CDataTable, CRow, CCol, CSwitch, CSidebar } from '@coreui/react';
import { ISorter, IPaginationParams, IFilter, FilterTypes, FilterByOption, FormField } from '@transferz/models';
import { ItemsPerPage } from './ItemsPerPage';
import { DataTableFilter } from './DataTableFilter';
import { DataTableHeader } from './DataTableHeader';
import { DataTablePagination } from './DataTablePagination';
import { Button } from '../button/Button';
import { Loading } from '../loading/Loading';
import { Form } from '../dataForm/Form';
import { isObject } from '@transferz/utils';
import { getFlatKeys, getQueryBody, constructFiltersFormFields } from './Table';
interface IDataTableProps {
  data: any;
  fields: any;
  totalEntries?: number;
  filterByOptions?: { label: string; key: string; }[];
  onPaginationChange?: ({ page, size }: IPaginationParams) => void;
  onFilterChange?: ({ filterBy, filterValue }: IFilter) => void;
  onSorterChange?: (values: ISorter[]) => void;
  filterValue?: string;
  tableSorterValues?: ISorter[];
  filterBy?: string;
  itemsPerPage?: number;
  showItemsPerPage?: boolean;
  showFilter?: boolean;
  showToggle?: boolean;
  toggleLabel?: string;
  isToggleOn?: boolean;
  setIsToggleOn?: (value: boolean) => void;
  activePage?: number;
  loading?: boolean;
  showPagination?: boolean;
  autocompleteResults?: any[];
  reflectFiltersInUrl?: boolean;
  onOptionsSearch?: (query: string) => void;
  onActiveFilterChanged?: (filter: string) => void;
  [key: string]: any;
  showSideFilters?: boolean;
  filtersFormId?: string;
  sidebarFilterOptions?: FilterByOption[];
  defaultParams?: any;
  onSidebarFilterChange?: (filters: {}[]) => void;
  transformQueries?: (arg: any) => any;
  filterOnlyDay?: boolean;
  showRemoveFilterIcon?: boolean;
}

export const DataTable: React.FunctionComponent<IDataTableProps> = ({
  data,
  fields,
  onFilterChange,
  totalEntries = 0,
  onPaginationChange,
  onSorterChange,
  tableSorterValues = [],
  itemsPerPage = 10,
  filterByOptions,
  filterValue = '',
  filterBy = '',
  showItemsPerPage = true,
  showFilter = true,
  showToggle = false,
  isToggleOn,
  setIsToggleOn,
  toggleLabel = '',
  activePage = 1,
  loading,
  showPagination = true,
  noItemsViewText = 'No items',
  autocompleteResults = [],
  onOptionsSearch,
  onActiveFilterChanged,
  reflectFiltersInUrl = false,
  filtersFormId = 'allFiltersForm',
  sidebarFilterOptions = [],
  defaultParams = {},
  showSideFilters = false,
  transformQueries,
  showRemoveFilterIcon = true,
  onSidebarFilterChange,
  filterOnlyDay = false,
  ...props
}) => {
  const history = useHistory();
  const urlQueryParams = new URLSearchParams(useLocation().search);
  const sidebarRef = useRef(null) as any;
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [shouldRerender, setShouldRerender] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [filtersFormFields, setFiltersFormFields] = useState([]) as any;
  
  const [queries, setQueries] = useState<any>({
    page: 0,
    size: defaultParams?.size || 10,
  });
  const queryBody = getQueryBody(queries, defaultParams, urlQueryParams, sidebarFilterOptions);

  const handleDocumentClick = (event: any) => {
    if (!sidebarRef.current.contains(event.target) && document.body.contains(event.target)) {
      setShowFilterBar(false);
    }
  };

  useEffect(() => {
    if (showSideFilters) {
      const formFields = constructFiltersFormFields(sidebarFilterOptions, urlQueryParams, defaultParams);
      setFiltersFormFields(formFields);
      if (queryBody) {
        let appliedFilters = Object.keys(queryBody).filter((key: string) => key !== 'page' && key !== 'size' && key !== 'sort');
        let filterCount = appliedFilters.length;
        if(appliedFilters.includes('pickupDateBefore') && appliedFilters.includes('pickupDateAfter')) {
          filterCount -= 1; // If both pickupDateAfter and pickupDateBefore are present, count as one filter
        }
        if(appliedFilters.includes('createdBefore') && appliedFilters.includes('createdAfter')) {
          filterCount -= 1; // If both createdBefore and createdAfter are present, count as one filter
        }
        setFilterCount(filterCount);
      }
      document.addEventListener('click', handleDocumentClick);
      return () => {
        document.removeEventListener('click', handleDocumentClick);
      };
    }
  }, [urlQueryParams.toString()]);

  const clearAllFilters = () => {
    const clearedQueries: any = { ...queryBody };
    const flat: string[] = getFlatKeys(sidebarFilterOptions.filter((option: any) => option.type !== FilterTypes.dynamicSearch));
    flat.forEach((key: string) => {
      if (urlQueryParams.toString().indexOf(key) !== -1) {
        urlQueryParams.delete(key);
      }
      delete clearedQueries[key];
    });
    setQueries({
      ...clearedQueries,
    });
    history.push({
      search: urlQueryParams.toString(),
    });
    if (onSidebarFilterChange) {
      onSidebarFilterChange([]);
    }
    setShouldRerender(true);
    setTimeout(() => setShouldRerender(false));
  };

  const setUrlQueryParams = (item: any, type: string, removedFilters?: any) => {
    if (type === 'filter') {
      if (removedFilters) {
        removedFilters.forEach((filter: string) => {
          urlQueryParams.delete(filter);
        });
      }
      Object.keys(item).forEach((_item: any) => urlQueryParams.set(_item, JSON.stringify(item[_item])));
    } else if (type === 'pagination') {
      urlQueryParams.set('page', JSON.stringify(item.page));
      urlQueryParams.set('size', JSON.stringify(item.size));
    } else if (type === 'sort') {
      urlQueryParams.set('sort', JSON.stringify(item));
    }
    history.push({
      search: type !== 'clear' ? urlQueryParams.toString() : '',
    });
  };

  return (
  <div className="table-wrapper">
    {(showItemsPerPage || showFilter) && (
      <CRow className="mb-2">
        {showItemsPerPage && (
          <CCol>
            <ItemsPerPage
              itemsPerPage={itemsPerPage}
              onChange={(size) => {
                if (onPaginationChange) {
                  onPaginationChange({ size, page: 1 });
                }
              }}
            />
          </CCol>
        )}
        {showFilter && filterByOptions && (
          <CCol sm="8">
            <DataTableFilter
              showToggle={showToggle}
              toggleLabel={toggleLabel}
              isToggleOn={isToggleOn}
              setIsToggleOn={setIsToggleOn}
              onActiveFilterChanged={onActiveFilterChanged}
              filterValue={filterValue}
              filterBy={filterBy}
              filterByOptions={filterByOptions}
              reflectFiltersInUrl={reflectFiltersInUrl}
              autocompleteResults={autocompleteResults}
              onOptionsSearch={onOptionsSearch}
              onFilterChange={(filterValue: any, activeFilter: string) => {
                if (onFilterChange) {
                  onFilterChange({ filterValue, filterBy: activeFilter });
                }
              }}
              showRemoveFilterIcon={showRemoveFilterIcon}
            />
          </CCol>
        )}
        {showSideFilters && (
          <CCol sm="1" className="d-flex justify-content-end">  
          <Button
            className="pr-4 pl-4 full-width"
            title="Filter"
            color="info"
            onClick={() => setTimeout(() => setShowFilterBar(true))}
          />
          <div className="filter-count">
              {filterCount}
            </div>
          </CCol>
        )}
      </CRow>
    )}

    {showSideFilters && (
      <div ref={sidebarRef}>
        <CSidebar
          aside={true}
          show={showFilterBar}
          size="lg"
          colorScheme="light"
          onShowChange={() => setShowFilterBar(false)}
          className="filter-bar"
        >
          <div className="filter-header">
            <span className="filter-title">Filter</span>
            <span className='close-btn' onClick={() => setShowFilterBar(false)}>X</span>
          </div>
          <div className="filter-actions">
            <Button
              title="Apply filters"
              color="success"
              type="submit"
              form={filtersFormId}
            />
            <Button
              title="Remove all filters"
              color="danger"
              onClick={clearAllFilters}
            />
          </div>
          {shouldRerender ? (
            <Loading />
          ) : (
            <Form
              id={filtersFormId}
              filterOnlyDay={filterOnlyDay}
              data={{}}
              fields={filtersFormFields.filter((option: any) => option.type !== FilterTypes.dynamicSearch)}
              onSubmit={async (formData: any) => {
                let submitData: any = {};
                const shouldApplyFilters = Object.keys(formData).some((key) => formData[key]);
                if (shouldApplyFilters) {
                  
                  Object.keys(formData).forEach((key: string) => {
                    if (isObject(formData[key]) && formData[key].value && (typeof formData[key].value === 'string' || typeof formData[key].value === 'number'
                      || typeof formData[key].value === 'boolean')) {
                      submitData[key] = formData[key].value;
                    } else if (isObject(formData[key]) && formData[key]?.startDate || formData[key]?.endDate) {
                      const option = sidebarFilterOptions.find((option: any) => option.key === key);
                      if (option?.args?.fields?.length) {
                        if (formData[key]?.startDate) {
                          submitData[option.args.fields[0]] = formData[key].startDate;
                        }
                        if (formData[key]?.endDate) {
                          submitData[option.args.fields[1]] = formData[key].endDate;
                        }
                      }
                    } else if (Array.isArray(formData[key])) {
                      formData[key].forEach((item: any) => {
                        if (isObject(item) && item.value) {
                          submitData[key] = submitData[key] || [];
                          submitData[key].push(item.value);
                        }
                      });
                    } else if (formData[key]) {
                      submitData[key] = formData[key];
                    }
                  });
                  if ((typeof transformQueries === 'function')) {
                    submitData = transformQueries(submitData);
                  }
                  const clearedQueries: any = { ...queryBody, ...submitData };
                  const removedFilters: any = [];
                  Object.keys(queryBody).forEach((key: string) => {
                    if (formData.hasOwnProperty(key) && !formData[key]) {
                      removedFilters.push(key);
                      delete clearedQueries[key];
                    }
                  });
                  setQueries({
                    ...clearedQueries,
                  });
                  if (onSidebarFilterChange) {
                    let filtersArray: {}[] = [];
                    Object.keys(submitData).forEach((key: string) => {
                      if (submitData[key]) {
                        filtersArray.push({
                          key,
                          value: typeof submitData[key] === 'object' ? (submitData[key]).value : submitData[key],
                        });
                      }
                    });
                    onSidebarFilterChange(filtersArray);
                  }
                  setUrlQueryParams(submitData, 'filter', removedFilters);
                } else {
                  const clearedQueries: any = { ...queryBody };
                  Object.keys(queryBody).forEach((key: string) => {
                    if (formData.hasOwnProperty(key) && !defaultParams.hasOwnProperty(key)) {
                      delete clearedQueries[key];
                    }
                  });
                  setQueries({
                    ...clearedQueries,
                  });
                  setUrlQueryParams({}, 'clear');
                }
                setTimeout(() => setShowFilterBar(false));
              }}
            />
          )}
        </CSidebar>
      </div>
    )}
    <CDataTable
      items={data}
      fields={fields}
      header={false}
      striped={true}
      hover={true}
      theadTopSlot={(
        <DataTableHeader
          fields={fields}
          sorterValues={tableSorterValues}
          onSorterChange={onSorterChange}
        />
      )}
      underTableSlot={showPagination && (
        <DataTablePagination
          page={activePage}
          totalEntries={totalEntries}
          itemsPerPage={itemsPerPage}
          onChange={(page) => {
            if (onPaginationChange) {
              onPaginationChange({ size: itemsPerPage, page });
            }
          }}
        />
      )}
      loading={loading}
      noItemsView={{noResults: noItemsViewText, noItems: noItemsViewText }}
      noItemsViewSlot={loading && (!data || !data.length) ? <div className="emptyRows" /> : undefined}
      {...props}
    />
  </div>
  )
};

